import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import Illustrazione1 from '../ui/Illustrazione1.png';
import Illustrazione2 from '../ui/Illustrazione2.png';
import Illustrazione3 from '../ui/Illustrazione3.png';
import { SEO } from '../components/SEO';

function WorkWithUs() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Otteniamo l'indice basandoci sull'elemento DOM
          const index = sectionRefs.current.indexOf(entry.target as HTMLDivElement);

          // Se interseca e non è già stato segnato come visibile
          if (entry.isIntersecting && index !== -1) {
            setVisibleSections((prev) => {
              const newSet = new Set(prev);
              newSet.add(index);
              return newSet;
            });
            // Opzionale: smettiamo di osservare l'elemento una volta animato per performance
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.25,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const scrollToNext = () => {
    if (sectionRefs.current[0]) {
      sectionRefs.current[0].scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSection = (index: number) => {
    const section = sectionRefs.current[index];
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO
        title="Diventa Insegnante"
        description="Unisciti al team Formando PerCorsi: ti troviamo gli studenti, gestiamo la burocrazia. Lavoro flessibile per chi ama insegnare."
        canonical="https://formandopercorsi.com/lavora-con-noi"
      />
      <main className="flex flex-col w-full overflow-x-hidden bg-fpcwhite selection:bg-fpcred selection:text-white">
        {/* HERO SECTION */}
        <section className="relative flex flex-col justify-center items-center md:min-h-screen bg-fpcwhite px-4 lg:px-16 md:pb-20" aria-label="Diventa insegnante">
        <div className="flex flex-col-reverse lg:flex-row items-center justify-between max-w-7xl w-full mx-auto gap-8 lg:gap-12 flex-grow p-2 sm:p-4">
          <div className="flex flex-col gap-6 items-center lg:items-center lg:w-1/2 text-center z-20 relative order-2 lg:order-2">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-fpcred/10 border border-fpcred/20 mb-2">
              <span className="text-xs md:text-sm font-bold text-fpcred tracking-wide uppercase">Unisciti al team</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-gray-900 tracking-tight leading-[1.1]">
              Diventa un <br />
              <span className="text-fpcred">insegnante</span>
            </h1>

            <h2 className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-xl">
              Vuoi metterti in gioco e trasformare le tue competenze in un lavoro utile e gratificante?
              <span className="hidden sm:inline">
                <br />
              </span>
              Con <span className="text-gray-900 font-bold">Formando Percorsi</span> cresci insieme ai tuoi studenti.
            </h2>

            <div className="hidden lg:flex flex-col sm:flex-row gap-4 mt-2 sm:w-auto">
              <button
                onClick={scrollToNext}
                className="px-8 py-3.5 rounded-full bg-fpcred text-white font-bold text-base lg:text-lg shadow-lg hover:scale-105 transition-transform duration-300">
                <span className="flex items-center justify-center gap-2">Scopri di più</span>
              </button>
            </div>
          </div>

          <div className="relative flex items-center justify-center lg:w-1/2 w-full order-1 lg:order-1">
            <div className="relative w-full flex justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100%] h-[100%] bg-gradient-to-tr from-fpcred/10 to-fpcyellow/20 rounded-full blur-3xl -z-10"></div>
              <img
                src={Illustrazione1}
                alt="Insegnanti Formando Percorsi"
                className="relative z-10 w-auto h-auto max-h-[40vh] sm:max-h-[50vh] lg:max-h-[65vh] object-contain drop-shadow-xl"
              />
            </div>
          </div>
        </div>

        <div className="w-full flex justify-center md:mt-4">
          <button
            onClick={scrollToNext}
            className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
            aria-label="Scorri giù">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>

      {/* SEZIONE 1: CHI CERCHIAMO */}
      <section
        ref={(el) => (sectionRefs.current[0] = el as HTMLDivElement)}
        className="min-h-svh lg:min-h-screen flex flex-col justify-center py-12 lg:py-16 px-6 overflow-hidden bg-white relative">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          <div
            className={`order-2 md:order-1 transition-all duration-700 ease-out ${
              visibleSections.has(0) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}>
            <div className="inline-block px-3 py-1 bg-gray-100 rounded-full text-xs font-bold tracking-widest text-gray-500 uppercase mb-4">
              Il Profilo
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">Chi cerchiamo</h2>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed mb-8">
              Cerchiamo persone <span className="text-fpcred font-semibold">motivate</span>, curiose e disponibili a
              imparare un nuovo modo di lavorare.
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="p-1 bg-green-100 rounded-full shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-gray-700 text-base lg:text-2xl">Non serve essere già insegnanti.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-1 bg-green-100 rounded-full shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-gray-700 text-base lg:text-2xl">Ti accompagniamo passo dopo passo.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-1 bg-green-100 rounded-full shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-gray-700 text-base lg:text-2xl">Costruiamo insieme le basi del tuo percorso.</p>
              </div>
            </div>
          </div>
          <div
            className={`order-1 md:order-2 flex justify-center transition-all duration-700 ease-out delay-200 ${
              visibleSections.has(0) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}>
            <div className="relative w-full max-w-2xl mx-auto">
              <div className="absolute inset-0 bg-fpcred/5 rounded-full blur-3xl transform rotate-6"></div>
              <img
                src={Illustrazione2}
                alt="Chi cerchiamo illustrazione"
                className="relative z-10 w-full h-auto object-contain hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>

        {/* Freccia per sezione successiva */}
        <div className="w-full flex justify-center mt-4 md:mt-8">
          <button
            onClick={() => scrollToSection(1)}
            className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
            aria-label="Scorri verso il basso">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>

      {/* SEZIONE 2: PERCHÉ NOI (CARDS) */}
      <section
        ref={(el) => (sectionRefs.current[1] = el as HTMLDivElement)}
        // Applicata stessa logica di velocità anche qui
        className={`min-h-svh lg:min-h-screen flex flex-col justify-center py-12 lg:py-16 px-6 bg-gray-50 transition-all duration-700 ease-out will-change-transform ${
          visibleSections.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-24'
        }`}>
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Perché diventare Insegnante?</h2>
            <p className="text-lg lg:text-xl text-gray-500">
              Tutti gli strumenti per insegnare al meglio, senza stress.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Card 1 */}
            <div className="bg-white p-6 lg:p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-fpcred/5 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110"></div>
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-6 text-fpcred">
                <Users className="w-6 h-6 lg:w-8 lg:h-8" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                Pensiamo noi a <span className="text-fpcred">trovarti</span> gli studenti
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm lg:text-xl">
                Ti abbiniamo agli studenti perfetti per te, così tu potrai concentrarti su ciò che conta davvero:{' '}
                <span className="text-fpcred font-semibold">insegnare.</span>
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-6 lg:p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-fpcyellow/10 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110"></div>
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-yellow-50 rounded-2xl flex items-center justify-center mb-6 text-yellow-600">
                <Briefcase className="w-6 h-6 lg:w-8 lg:h-8" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                Lascia a noi il <span className="text-fpcred">lavoro pesante</span>
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm lg:text-xl">
                La piattaforma si occupa di tutta la{' '}
                <span className="text-fpcred font-semibold">burocrazia e della gestione dei pagamenti</span>, così tu
                dovrai solo preoccuparti di insegnare.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-6 lg:p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110"></div>
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 text-blue-600">
                <TrendingUp className="w-6 h-6 lg:w-8 lg:h-8" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                Perfetto per la tua <span className="text-fpcred">crescita</span>
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm lg:text-xl">
                Le ripetizioni sono facilmente <span className="text-fpcred font-semibold">conciliabili</span> con i
                tuoi impegni part-time.
              </p>
            </div>
          </div>
        </div>

        {/* Freccia per sezione successiva */}
        <div className="w-full flex justify-center mt-4 md:mt-8">
          <button
            onClick={() => scrollToSection(2)}
            className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcpink transition-colors"
            aria-label="Scorri verso il basso">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>

      {/* SEZIONE 3: COME FUNZIONA */}
      <section
        ref={(el) => (sectionRefs.current[2] = el as HTMLDivElement)}
        className={`min-h-svh lg:min-h-screen flex flex-col justify-center py-12 lg:py-16 px-6 bg-fpcpink overflow-hidden transition-all duration-700 ease-out will-change-transform ${
          visibleSections.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-24'
        }`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 lg:gap-16 w-full">
          <div className="flex-1 flex justify-center items-center relative w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-fpcred/5 to-transparent rounded-full blur-3xl"></div>
            <img
              src={Illustrazione3}
              alt="Come funziona illustrazione"
              className="relative z-10 w-full max-w-sm lg:max-w-2xl object-contain drop-shadow-xl"
            />
          </div>
          <div className="flex-1">
            <div className="inline-block px-3 py-1 bg-gray-100 rounded-full text-xs font-bold tracking-widest text-gray-500 uppercase mb-4">
              Processo
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Come funziona</h2>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed mb-6">
              Con Formando Percorsi impari sul campo{' '}
              <span className="text-fpcred font-semibold">a diventare un insegnante</span>.
            </p>
            <div className="prose prose-lg text-gray-600 leading-relaxed text-xl">
              <p>
                <span className="text-fpcred font-semibold">Ti affianchiamo</span>, ti forniamo{' '}
                <span className="text-fpcred font-semibold">strumenti</span> e ti mettiamo in contatto con{' '}
                <span className="text-fpcred font-semibold">studenti e famiglie</span>. Lavori in{' '}
                <span className="text-fpcred font-semibold">autonomia</span>, ma con il supporto di un team che crede
                nelle tue potenzialità e{' '}
                <span className="text-fpcred font-semibold">ti libera dalle incombenze burocratiche</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Freccia per sezione successiva */}
        <div className="w-full flex justify-center pb-4">
          <button
            onClick={() => scrollToSection(3)}
            className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-fpcred/10 transition-colors"
            aria-label="Scorri verso il basso">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-fpcred"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>

      {/* CTA FINALE */}
      <section
        ref={(el) => (sectionRefs.current[3] = el as HTMLDivElement)}
        className="min-h-[85vh] md:min-h-screen flex flex-col justify-center py-8 md:py-12 lg:py-16 px-6 bg-fpcred relative overflow-hidden text-center">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 rounded-full bg-fpcyellow blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto z-10 text-white w-full">
          <h2 className="text-3xl md:text-6xl font-bold mb-6 lg:mb-8 tracking-tight">Unisciti a noi</h2>
          <p className="text-lg md:text-2xl opacity-90 leading-relaxed mb-8 lg:mb-12 max-w-2xl mx-auto">
            Se vuoi crescere, metterti in gioco e trasformare la tua passione in un lavoro flessibile e stimolante.
          </p>

          <div className="flex flex-col items-center gap-6">
            <button
              onClick={() => navigate('/teacher-application')}
              className="inline-block px-8 py-4 lg:px-10 lg:py-5 bg-white text-fpcred font-bold text-lg lg:text-xl rounded-full shadow-2xl hover:scale-105 hover:shadow-white/20 transition-all duration-300 cursor-pointer">
              Invia la tua candidatura
            </button>
            <p className="text-white/80 text-base lg:text-xl mt-4 px-4">
              Con{' '}
              <span className="font-bold text-white underline decoration-fpcyellow decoration-4 underline-offset-4">
                Formando Percorsi
              </span>{' '}
              puoi diventare insegnante,
              <br className="hidden md:block" /> un passo alla volta.
            </p>
          </div>
        </div>

        {/* Freccia per tornare all'inizio */}
        <div className="w-full flex justify-center mt-4 md:mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="animate-bounce cursor-pointer p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Torna su">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-white rotate-180"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>
    </main>
    </>
  );
}

export default WorkWithUs;
